import { useAtomValue } from 'jotai';

import useAuth from '@/hooks/useAuth';
import usePlanOperators from '@/hooks/usePlanOperators';

import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

const usePlanOwner = () => {
  const { data: auth } = useAuth();
  const leaderCorpLocations = auth?.leaderCorpLocations ?? [];
  const tgLocations = auth?.supportCenterLocations.tgLocations ?? [];
  const { isFetching, operators } = usePlanOperators();

  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);

  const userId = auth?.userId ?? '';
  const isLicensee = auth?.isLicensee;

  const isMultiOwner =
    (operators.length || 0) + (leaderCorpLocations.length || 0) > 1;

  const shouldSelectOwner = isMultiOwner && !isLicensee;

  const licenseeLocationNumber = isLicensee
    ? activeLicenseeLocation?.number
    : undefined;

  /**
   * When `selection` is provided, resolves from explicit user choices.
   * When omitted, auto-resolves to the first available owner.
   */
  const resolvePlanOwner = (selection?: {
    locationNumber?: string;
    operatorId?: string;
  }) => {
    if (licenseeLocationNumber)
      return { locationNumber: licenseeLocationNumber };

    if (selection) {
      if (selection.locationNumber)
        return { locationNumber: selection.locationNumber };
      if (selection.operatorId) return { ownerId: selection.operatorId };
    } else {
      const firstCorp = leaderCorpLocations[0]?.number;
      if (firstCorp) return { locationNumber: firstCorp };

      const firstTg = tgLocations[0]?.id;
      if (firstTg) return { ownerId: firstTg };

      const firstOp = operators[0]?.id;
      if (firstOp) return { ownerId: firstOp };
    }

    return { ownerId: userId };
  };

  return {
    isFetching,
    isMultiOwner,
    shouldSelectOwner,
    resolvePlanOwner,
  };
};

export default usePlanOwner;
